import { track, updatePageProps } from '@eventbrite/datalayer-library';
import { guessUserTimezone } from '@eventbrite/datetime-fns';
import { Icon } from '@eventbrite/eds-icon';
import { ChevronDownChunky } from '@eventbrite/eds-iconography';
import { Layout } from '@eventbrite/eds-layout';
import { gettext } from '@eventbrite/i18n';
import { requestIdleCallbackWithPolyfill } from '@eventbrite/request-idle-callback';
import { logEvent } from '@eventbrite/statsig';
import React from 'react';
import { connect } from 'react-redux';
import { DateFilter } from '../../../../constants/dates';
import { IBrowseFilters } from '../../../../constants/filters';
import { FEED_TABS, FOR_YOU } from '../../../../constants/tabConfig';
import {
    GetEnrollmentStatus,
    useGetStatsigEnrollment,
} from '../../../../hooks';
import {
    ConnectedProps,
    DiscoverState,
    HomepageSeasonalCarouselExperiment,
    SeasonalContents,
} from '../../../../types/index';
import { StickyTabs } from '../../components/Tabs';
import {
    HomeEngagementExperimentTrackingLocations,
    STATSIG_HOME_ANY_CLICK,
} from '../../constants/analytics';
import { useBrowseFilters } from '../../hooks/BrowseFilters';
import { fetchEventsForTab, fetchUserSavedEvents } from '../../redux/events/';
import { injectSeasonalTab, scrollIt } from '../../utils';
import { logToStatsigHomeEngagement } from '../../utils/analytics';
import { FeedBuilderContainer } from '../FeedBuilderContainer';
import { HomeLocationAutocompleteContainer } from '../HomeLocationAutocompleteContainer';
import { IconCategoryBrowse } from '../IconCategoryBrowse';
import { ReferralCreditMessage } from '../ReferralCreditMessage';
import SeasonalModule from '../SeasonalModule/SeasonalModule';
import './BrowseContainer.scss';
import { ForYouTabKey } from './ForYouTabKey';
const Chevron = (
    <Icon type={<ChevronDownChunky />} color="ui-blue--hover" size="medium" />
);

export const ONLINE_EVENTS_INPUT_COPY = gettext('Popular ');

const LOCATION_AUTOCOMPLETE_PROPS = {
    prefix: Chevron,
    borderType: 'content',
    id: 'locationPicker',
    name: 'locationPicker',
    hideOutline: true,
    isLimitedSearch: true,
    preventInputValuePrefill: true,
    shouldRemoveConflictiveAria: true,
};

const shouldIncludeCurrentTab = ({
    tabKey,
    tabsConfig,
}: {
    tabKey?: string;
    tabsConfig: { [key: string]: any };
}) => {
    if (!tabKey || !tabsConfig[tabKey]) {
        return false;
    }

    return true;
};

const getBrowseTabs = (
    dateFilter: DateFilter,
    seasonalContent?: SeasonalContents,
    locationSlug?: string,
    homepageSeasonalCarouselVariant?: boolean | string,
) => {
    const tabsConfig = injectSeasonalTab(seasonalContent);
    const tabs: {
        tabKey: string;
        value: number;
        displayName: string;
        content: JSX.Element;
    }[] = [];

    FEED_TABS.forEach((tabKey, index) => {
        const shouldAddTab = shouldIncludeCurrentTab({
            tabKey,
            tabsConfig,
        });

        if (shouldAddTab) {
            const { name, eventSearch, follow, feedConfig } =
                tabsConfig[tabKey];
            let displayName = name;

            if (tabKey === FOR_YOU) {
                displayName = <ForYouTabKey />;
            }

            tabs.push({
                tabKey,
                value: index,
                displayName: displayName,
                content: (
                    <FeedBuilderContainer
                        feedConfig={feedConfig}
                        eventSearch={eventSearch}
                        follow={follow}
                        tabKey={tabKey}
                        homepageSeasonalCarouselVariant={
                            homepageSeasonalCarouselVariant
                        }
                        dateFilter={dateFilter}
                        locationSlug={locationSlug}
                    />
                ),
            });
        }
    });

    return tabs;
};

const LocationComponent = ({ location }: { location?: object }) => {
    const copy = gettext('Browsing events in ');

    // Homepage location pill experiment clean up. EB-229970

    return (
        <div className="browse__location eds-l-pad-top-4">
            <div className="browse__location-text">{copy}</div>
            <HomeLocationAutocompleteContainer
                autocompleteProps={LOCATION_AUTOCOMPLETE_PROPS}
                shouldDispatchSearchActions={true}
                location={location}
            />
        </div>
    );
};

const handleLocationChange = (
    tabKey: string,
    {
        fetchEventsForTab,
        filters,
    }: {
        fetchEventsForTab: Function;
        filters?: IBrowseFilters[];
    },
) => {
    fetchEventsForTab({ tabKey, filters });
};

const handleTabLoad = (
    tabKey: string,
    {
        fetchEventsForTab,
        filters,
    }: {
        fetchEventsForTab: Function;
        filters?: IBrowseFilters[];
    },
) => {
    fetchEventsForTab({ tabKey, filters });
};

const handleTabChange = (
    tabKey: string,
    {
        fetchEventsForTab,
        filters,
    }: {
        fetchEventsForTab: Function;
        filters?: IBrowseFilters[];
    },
) => {
    scrollIt(
        ((document.querySelector('.browse-section') as HTMLElement)
            ?.offsetTop || 0) + 70,
    );

    fetchEventsForTab({ tabKey, filters });
};

const getUserTimezoneFilter = (
    weekendGuideExperimentVariant: boolean | string,
) => {
    if (weekendGuideExperimentVariant) {
        return [
            {
                name: 'timezone',
                value: guessUserTimezone(),
                payload: 'data',
            },
        ];
    }
    return undefined;
};

const handleOnClick = () => {
    logEvent(STATSIG_HOME_ANY_CLICK);
};

const Browse = (props: BrowseProps) => {
    const {
        tabKey,
        location,
        seasonalContent,
        loadingState,
        featureFlags,
        fetchEventsForTab,
        fetchUserSavedEvents,
        isAuthenticated,
        isMobile,
    } = props;
    //TODO: Pass proper enum in for expected variant values

    const weekendGuideExperiment = useGetStatsigEnrollment<string | boolean>({
        name: 'weekend_guide_v2',
        paramName: 'weekend_guide',
        defaultValue: 'hide',
        enabled: tabKey === FOR_YOU,
    });

    const weekendGuideExperimentVariant =
        weekendGuideExperiment.status === GetEnrollmentStatus.SUCCESS
            ? weekendGuideExperiment.variant === 'show'
            : GetEnrollmentStatus.LOADING;

    const homepageSeasonalCarouselExperiment = useGetStatsigEnrollment({
        name: HomepageSeasonalCarouselExperiment.name,
        paramName: HomepageSeasonalCarouselExperiment.paramName,
        defaultValue: HomepageSeasonalCarouselExperiment.values.control,
    });

    const homepageSeasonalCarouselVariant =
        homepageSeasonalCarouselExperiment.status ===
        GetEnrollmentStatus.SUCCESS
            ? homepageSeasonalCarouselExperiment.variant ===
              HomepageSeasonalCarouselExperiment.values.test
            : GetEnrollmentStatus.LOADING;

    const userTimezoneFilter = getUserTimezoneFilter(
        weekendGuideExperimentVariant,
    );

    const dateFilter = useBrowseFilters(fetchEventsForTab);
    const items = getBrowseTabs(
        dateFilter,
        seasonalContent,
        location.slug,
        homepageSeasonalCarouselVariant,
    );
    const hasFetchedEventsRef = React.useRef(false);

    React.useEffect(() => {
        if (!hasFetchedEventsRef.current && tabKey === FOR_YOU) {
            hasFetchedEventsRef.current = true;
            requestIdleCallbackWithPolyfill(() => fetchUserSavedEvents());
        }
    }, [fetchUserSavedEvents, tabKey]);

    React.useEffect(() => {
        if (location && location.currentPlace) {
            track({
                eventName: 'HomepageSearchLocation',
                eventData: {
                    searchLocation: location.currentPlace,
                },
            });
        }
    }, [location]);

    React.useEffect(() => {
        if (location && location.currentPlace) {
            updatePageProps({
                eventName: 'SearchLocationView',
                page: {
                    searchLocation: location.currentPlace,
                },
            });
        }
    }, [location]);

    React.useEffect(() => {
        if (tabKey) {
            track({
                eventName: 'HomeTabView',
                eventData: { homeTabName: tabKey },
            });
        }
    }, [tabKey]);

    const isLoading = Boolean(loadingState?.isFeLoading);

    return (
        <div
            className="browse-section"
            id="browse-section"
            onClick={handleOnClick}
        >
            <IconCategoryBrowse isMobile={isMobile} />

            <ReferralCreditMessage />
            <hr />
            <Layout hasHorizontalGutters={true} maxWidth="large">
                <LocationComponent location={location} />
            </Layout>
            <hr />
            {featureFlags.enableSeasonalModule && (
                <Layout hasHorizontalGutters={!isMobile} maxWidth="large">
                    <SeasonalModule seasonal="VALENTINE" />
                </Layout>
            )}

            {!isLoading && (
                <StickyTabs
                    items={items}
                    isNewHeader={featureFlags.launchHeader2021}
                    onTabChange={(tabKey: string) => {
                        const trackingLocation =
                            tabKey === FOR_YOU
                                ? HomeEngagementExperimentTrackingLocations.FOR_YOU
                                : HomeEngagementExperimentTrackingLocations.TAB;

                        logToStatsigHomeEngagement(
                            trackingLocation,
                            isAuthenticated,
                        );

                        handleTabChange(tabKey, {
                            fetchEventsForTab,
                            filters: userTimezoneFilter,
                        });
                    }}
                    onLocationChange={(tabKey: string) =>
                        handleLocationChange(tabKey, {
                            fetchEventsForTab,
                            filters: userTimezoneFilter,
                        })
                    }
                    onTabLoad={(tabKey: string) =>
                        handleTabLoad(tabKey, {
                            fetchEventsForTab,
                            filters: userTimezoneFilter,
                        })
                    }
                    initialSelectedTab={tabKey}
                    location={location}
                    dateFilter={dateFilter}
                />
            )}
        </div>
    );
};

const _mapStateToProps = (state: DiscoverState) => ({
    isAuthenticated: Boolean(state.user.isAuthenticated),
    isMobile: state.env.isMobile,
    tabKey: state.content.browseState?.tabKey,
    loadingState: state.app.loadingState,
    location: state.location,
    seasonalContent: state.content.seasonal,
    featureFlags: state.app.featureFlags,
});

const _mapDispatchToProps = {
    fetchEventsForTab,
    fetchUserSavedEvents,
};

type BrowseProps = ConnectedProps<
    typeof _mapStateToProps,
    typeof _mapDispatchToProps
>;

export default connect(_mapStateToProps, _mapDispatchToProps)(Browse);
